import React, { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'

import { Button } from 'components/ui/button'
import { getUnsubscribeLink, unsubscribe, type UnsubscribeLink } from '../services/unsubscribeService'

/**
 * Where an email's "Unsubscribe" link lands (MYD-546). No login: the token in
 * the URL is the credential.
 *
 * Opening the page only reads. The person confirms before anything changes,
 * because mail scanners fetch every link in a message, and a page that
 * unsubscribed on load would turn email off for people who never clicked.
 * The mail client's own Unsubscribe button does not come here; it posts
 * straight to the server.
 */

const SETTINGS = '/admin/userprofile#notifications'

const errorMessage = (err: any, fallback: string) => err?.response?.data?.message ?? fallback

type Done = { all: boolean; label: string }

const UnsubscribeScreen = () => {
	const [params] = useSearchParams()
	const token = params.get('token') ?? ''
	const [link, setLink] = useState<UnsubscribeLink | null>(null)
	const [loadError, setLoadError] = useState<string | null>(null)
	const [saving, setSaving] = useState<'one' | 'all' | null>(null)
	const [saveError, setSaveError] = useState<string | null>(null)
	const [done, setDone] = useState<Done | null>(null)

	useEffect(() => {
		if (!token) {
			setLoadError('This unsubscribe link is not valid.')
			return
		}
		let live = true
		getUnsubscribeLink(token)
			.then(data => live && setLink(data))
			.catch(err => live && setLoadError(errorMessage(err, 'This unsubscribe link is not valid.')))
		return () => {
			live = false
		}
	}, [token])

	const confirm = async (all: boolean) => {
		setSaving(all ? 'all' : 'one')
		setSaveError(null)
		try {
			const result = await unsubscribe(token, all)
			setDone({ all, label: result.label })
		} catch (err) {
			setSaveError(errorMessage(err, 'That did not work. Please try again.'))
		} finally {
			setSaving(null)
		}
	}

	const settingsLink = (
		<Link to={SETTINGS} className="text-sm text-primary">
			Manage all email settings
		</Link>
	)

	let content: React.ReactNode
	if (loadError) {
		content = (
			<>
				<h1 className="mb-2 text-xl font-bold text-foreground">Link not valid</h1>
				<p className="mb-6 text-sm text-muted-foreground">{loadError} You can still turn emails off in your account settings.</p>
				{settingsLink}
			</>
		)
	} else if (!link) {
		content = <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" aria-label="Loading" />
	} else if (done) {
		content = (
			<>
				<h1 className="mb-2 text-xl font-bold text-foreground">You are unsubscribed</h1>
				<p className="mb-6 text-sm text-muted-foreground">
					{done.all
						? `We will not send ${link.email} any optional email. Security and account email still arrives.`
						: `We will not email ${link.email} when this happens: ${done.label.toLowerCase()}. You will still see it in the app.`}
				</p>
				{settingsLink}
			</>
		)
	} else {
		const allAlready = link.paused
		content = (
			<>
				<h1 className="mb-2 text-xl font-bold text-foreground">Unsubscribe</h1>
				{link.subscribed && !allAlready ? (
					<p className="mb-6 text-sm text-muted-foreground">
						Stop emailing {link.email} when this happens: <span className="font-medium text-foreground">{link.label.toLowerCase()}</span>?
					</p>
				) : (
					<p className="mb-6 text-sm text-muted-foreground">
						{allAlready ? `Optional email to ${link.email} is already paused.` : `${link.email} is already unsubscribed from these emails.`}
					</p>
				)}
				<div className="flex flex-col items-center gap-3">
					{link.subscribed && !allAlready && (
						<Button isLarge className="text-sm" disabled={saving !== null} onClick={() => confirm(false)}>
							{saving === 'one' && <Loader2 className="animate-spin" />}
							Unsubscribe
						</Button>
					)}
					{!allAlready && (
						<Button variant="ghost" isOutline className="px-4 py-2 text-sm" disabled={saving !== null} onClick={() => confirm(true)}>
							{saving === 'all' && <Loader2 className="animate-spin" />}
							Pause all optional emails
						</Button>
					)}
				</div>
				{saveError && (
					<p role="alert" className="mt-4 text-sm text-destructive">
						{saveError}
					</p>
				)}
				<div className="mt-6">{settingsLink}</div>
			</>
		)
	}

	return (
		<div className="flex min-h-screen items-center justify-center bg-muted px-4">
			<div className="w-full max-w-md rounded-xl border border-border bg-background p-8 text-center shadow-sm">{content}</div>
		</div>
	)
}

export default UnsubscribeScreen
